import React, { useEffect, useState } from 'react';
import { authService } from '../../services/api';
import styles from './ActivityLogs.module.css';

interface LogEntry {
  id: number;
  userEmail: string;
  userRole: string;
  method: string;
  route: string;
  statusCode: number;
  ipAddress: string;
  userAgent: string;
  entityType: string;
  entityId: string;
  createdAt: string;
}

interface PaginatedResponse {
  data: LogEntry[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

const methodColors: Record<string, string> = {
  GET: styles.methodGet,
  POST: styles.methodPost,
  PUT: styles.methodPut,
  DELETE: styles.methodDelete,
};

const ActivityLogs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const loadLogs = async (p: number) => {
    setLoading(true);
    setError('');
    try {
      const response: PaginatedResponse = await authService.listActivityLogs(p);
      setLogs(response.data);
      setTotal(response.total);
      setPage(response.page);
      setTotalPages(response.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(1);
  }, []);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const roleLabel = (role: string) => {
    const r = role.toUpperCase();
    if (r === 'MASTER') return 'Master';
    if (r === 'ADMIN') return 'Admin';
    if (r === 'TECNICO') return 'Técnico';
    return role;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Logs de Atividade</h1>
          <p className={styles.subtitle}>
            Registro de todas as ações realizadas no sistema. Apenas o Master tem acesso a esta página.
          </p>
        </div>
        <button
          className={styles.refreshButton}
          type="button"
          onClick={() => loadLogs(page)}
          disabled={loading}
        >
          {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </div>

      <div className={styles.card}>
        {error ? <div className={styles.error}>{error}</div> : null}

        {loading ? (
          <p className={styles.loading}>Carregando logs...</p>
        ) : !logs.length ? (
          <p className={styles.empty}>Nenhum log encontrado.</p>
        ) : (
          <>
            <div className={styles.totalInfo}>
              Total de registros: <strong>{total}</strong>
            </div>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Data/Hora</th>
                    <th>Usuário</th>
                    <th>Perfil</th>
                    <th>Método</th>
                    <th>Rota</th>
                    <th>Status</th>
                    <th>IP</th>
                    <th>Entidade</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td className={styles.dateCell}>{formatDate(log.createdAt)}</td>
                      <td>{log.userEmail}</td>
                      <td>
                        <span className={`${styles.roleBadge} ${log.userRole === 'MASTER' ? styles.roleMaster : log.userRole === 'ADMIN' ? styles.roleAdmin : styles.roleTecnico}`}>
                          {roleLabel(log.userRole)}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.methodBadge} ${methodColors[log.method] || ''}`}>
                          {log.method}
                        </span>
                      </td>
                      <td className={styles.routeCell} title={log.route}>
                        {log.route.length > 40 ? log.route.slice(0, 40) + '...' : log.route}
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${log.statusCode >= 200 && log.statusCode < 300 ? styles.statusSuccess : log.statusCode >= 400 ? styles.statusError : styles.statusOther}`}>
                          {log.statusCode}
                        </span>
                      </td>
                      <td className={styles.ipCell}>{log.ipAddress}</td>
                      <td>
                        {log.entityType ? (
                          <span className={styles.entityBadge}>
                            {log.entityType}#{log.entityId}
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageButton}
                  type="button"
                  onClick={() => loadLogs(page - 1)}
                  disabled={page <= 1 || loading}
                >
                  Anterior
                </button>
                <span className={styles.pageInfo}>
                  Página {page} de {totalPages}
                </span>
                <button
                  className={styles.pageButton}
                  type="button"
                  onClick={() => loadLogs(page + 1)}
                  disabled={page >= totalPages || loading}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
